// src/pages/Browser/Browser.js

import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WalletContext } from '../../contexts/WalletContext';
import { NetworkContext } from '../../contexts/NetworkContext';
import BrowserHeader from './components/BrowserHeader';
import BrowserHome from './components/BrowserHome';
import BrowserNavBar from './components/BrowserNavBar';
import secureFetch from '../../utils/secureFetch';
import './Browser.css';

const Browser = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [displayUrl, setDisplayUrl] = useState(''); // New state for displaying user-friendly URL
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [isShowingHome, setIsShowingHome] = useState(true);
  const [searchQuery, setSearchQuery] = useState(''); // Store the current search query
  
  // Custom navigation history stack
  const [navigationStack, setNavigationStack] = useState([]);
  const [currentNavigationIndex, setCurrentNavigationIndex] = useState(-1);
  
  // Add refs for tracking previous values to avoid unnecessary updates
  const iframeRef = useRef(null);
  const iframeContainerRef = useRef(null);
  
  // Tab management
  const [tabs, setTabs] = useState([{ url: '', displayUrl: '', title: '', favicon: null, isHome: true, isSearch: false }]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  
  const { currentWallet } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);

  // Listen for messages from the iframe (for search result clicks)
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      
      if (data && data.type === 'SEARCH_RESULT_CLICK' && data.url) {
        // Navigate to the clicked URL
        let targetUrl = data.url;
        
        // Ensure URL has a protocol
        if (!targetUrl.startsWith('http://') && !targetUrl.startsWith('https://')) {
          targetUrl = 'https://' + targetUrl;
        }
        
        // Update URL and display URL
        setUrl(targetUrl);
        setDisplayUrl(targetUrl);
        setIsLoading(true);
        
        // Update tab data
        updateTabData(targetUrl, targetUrl);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Create a hidden iframe for handling external navigation
  useEffect(() => {
    // Intercept all anchor clicks in our main iframe
    const interceptClicks = () => {
      // Check if our iframe exists and has a contentWindow
      if (!iframeRef.current || !iframeRef.current.contentWindow) return;

      try {
        // Attempt to access the iframe to inject our navigation handler
        const iframeDocument = iframeRef.current.contentDocument;
        if (!iframeDocument) return;
        
        // Create a MutationObserver to detect newly added links
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'childList') {
              // Find any newly added links
              const addedLinks = Array.from(mutation.addedNodes)
                .filter(node => node.nodeType === Node.ELEMENT_NODE)
                .flatMap(node => {
                  if (node.tagName === 'A') return [node];
                  return Array.from(node.getElementsByTagName('a'));
                });
              
              // Add event listeners to any new links
              addedLinks.forEach(link => {
                link.addEventListener('click', (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Log for debugging
                  console.log('Intercepted link click:', link.href);
                  
                  // Navigate to the link URL within our browser
                  if (link.href && link.href !== 'javascript:void(0)' && !link.href.startsWith('about:')) {
                    handleNavigate(link.href);
                  }
                  
                  return false;
                }, true);
              });
            }
          }
        });
        
        // Start observing the document
        observer.observe(iframeDocument, { 
          childList: true, 
          subtree: true 
        });
        
        // Add listeners to existing links
        const existingLinks = iframeDocument.getElementsByTagName('a');
        Array.from(existingLinks).forEach(link => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Log for debugging
            console.log('Intercepted link click:', link.href);
            
            // Navigate to the link URL within our browser
            if (link.href && link.href !== 'javascript:void(0)' && !link.href.startsWith('about:')) {
              handleNavigate(link.href);
            }
            
            return false;
          }, true);
        });
        
        // Also capture window.open calls
        const script = document.createElement('script');
        script.textContent = `
          window.originalOpen = window.open;
          window.open = function(url, name, features) {
            if (url && !url.startsWith('about:')) {
              window.parent.postMessage({ 
                type: 'WINDOW_OPEN', 
                url: url 
              }, '*');
              return null;
            }
            return window.originalOpen(url, name, features);
          };
        `;
        iframeDocument.head.appendChild(script);
        
        // Listen for window.open messages
        iframeRef.current.contentWindow.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'WINDOW_OPEN' && event.data.url) {
            handleNavigate(event.data.url);
          }
        });
      } catch (error) {
        console.error('Error setting up link interception:', error);
        // This is expected for cross-origin iframes due to security restrictions
        // We'll handle cross-origin navigation differently
      }
    };
    
    // Apply click interception after iframe loads
    if (iframeRef.current) {
      iframeRef.current.addEventListener('load', interceptClicks);
    }
    
    return () => {
      if (iframeRef.current) {
        iframeRef.current.removeEventListener('load', interceptClicks);
      }
    };
  }, [iframeRef.current]);
  
  // Track browsing history with useCallback to ensure stability
  const addToHistory = useCallback((historyUrl, historyTitle, isSearchPage = false) => {
    try {
      // Don't add data URLs to history
      if (historyUrl.startsWith('data:')) return;
      
      // Get stored history
      const savedHistory = localStorage.getItem('bitdog_browser_history');
      let history = savedHistory ? JSON.parse(savedHistory) : [];
      
      // Create timestamp
      const now = new Date();
      const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      // Create history entry
      const entry = {
        url: historyUrl,
        title: historyTitle || (isSearchPage ? searchQuery : new URL(historyUrl).hostname),
        time: timeStr,
        timestamp: now.getTime(),
        isSearch: isSearchPage
      };
      
      // Check if URL already exists in history
      const existingIndex = history.findIndex(item => item.url === historyUrl);
      if (existingIndex !== -1) {
        // Remove the existing entry
        history.splice(existingIndex, 1);
      }
      
      // Add to beginning of history array
      history.unshift(entry);
      
      // Limit history to 20 items
      if (history.length > 20) {
        history = history.slice(0, 20);
      }
      
      // Save updated history
      localStorage.setItem('bitdog_browser_history', JSON.stringify(history));
    } catch (error) {
      console.error('Error updating browser history:', error);
    }
  }, [searchQuery]);

  // Initialize tabs from local storage
  useEffect(() => {
    const savedTabs = localStorage.getItem('bitdog_browser_tabs');
    if (savedTabs) {
      try {
        const parsedTabs = JSON.parse(savedTabs);
        if (parsedTabs && parsedTabs.length > 0) {
          setTabs(parsedTabs);
          
          // Find first tab that is home or set to first tab
          const homeTabIndex = parsedTabs.findIndex(tab => tab.isHome);
          const selectedIndex = homeTabIndex >= 0 ? homeTabIndex : 0;
          setActiveTabIndex(selectedIndex);
          
          // Set current URL and title based on the active tab
          const selectedTab = parsedTabs[selectedIndex];
          setIsShowingHome(selectedTab.isHome || false);
          
          if (!selectedTab.isHome) {
            setUrl(selectedTab.url || '');
            setDisplayUrl(selectedTab.displayUrl || selectedTab.url || '');
            setTitle(selectedTab.title || '');
            if (selectedTab.isSearch && selectedTab.searchQuery) {
              setSearchQuery(selectedTab.searchQuery);
            }
          }
        } else {
          // Initialize with a home tab if parsed tabs are invalid
          setTabs([{ url: '', displayUrl: '', title: '', favicon: null, isHome: true, isSearch: false }]);
          setIsShowingHome(true);
        }
      } catch (error) {
        console.error('Error loading saved tabs:', error);
        // Initialize with a home tab if there's an error
        setTabs([{ url: '', displayUrl: '', title: '', favicon: null, isHome: true, isSearch: false }]);
        setIsShowingHome(true);
      }
    } else {
      // Initialize with a home tab
      setTabs([{ url: '', displayUrl: '', title: '', favicon: null, isHome: true, isSearch: false }]);
      setIsShowingHome(true);
    }
  }, []);

  // Save tabs to local storage when tabs change
  useEffect(() => {
    if (tabs.length > 0) {
      try {
        localStorage.setItem('bitdog_browser_tabs', JSON.stringify(tabs));
      } catch (error) {
        console.error('Error saving tabs to localStorage:', error);
      }
    }
  }, [tabs]);

  // Update tab data when URL or title changes
  useEffect(() => {
    if (activeTabIndex >= 0 && activeTabIndex < tabs.length) {
      // Only update if there's a URL (avoid overwriting with empty values)
      if (url) {
        const updatedTabs = [...tabs];
        
        // Check if the tab data actually changed to avoid unnecessary updates
        const currentTab = tabs[activeTabIndex];
        if (
          currentTab.url !== url || 
          currentTab.displayUrl !== displayUrl ||
          currentTab.title !== title || 
          currentTab.isHome !== isShowingHome ||
          (currentTab.isSearch && currentTab.searchQuery !== searchQuery)
        ) {
          updatedTabs[activeTabIndex] = {
            ...updatedTabs[activeTabIndex],
            url: url,
            displayUrl: displayUrl || url,
            title: title || url,
            isHome: isShowingHome,
            isSearch: url.startsWith('data:text/html'),
            searchQuery: url.startsWith('data:text/html') ? searchQuery : undefined
          };
          setTabs(updatedTabs);
        }
      }
    }
  }, [url, displayUrl, title, activeTabIndex, tabs, isShowingHome, searchQuery]);

  // Helper function to check if a string is likely a valid domain
  const isValidDomain = (str) => {
    // Simple check: contains a dot, no spaces, and has a valid TLD
    if (!str.includes('.') || str.includes(' ')) {
      return false;
    }
    
    // Check for common TLDs (this is a simplified check)
    const commonTLDs = [
      'com', 'org', 'net', 'edu', 'gov', 'io', 'co',
      'app', 'dev', 'info', 'biz', 'me', 'xyz', 'tv'
    ];
    
    const parts = str.split('.');
    const tld = parts[parts.length - 1].toLowerCase();
    
    return commonTLDs.includes(tld) || tld.length === 2; // Country code TLDs are typically 2 chars
  };

  // Helper function to update tab data
  const updateTabData = (processedUrl, displayUrlValue, customTitle = '', isSearchPage = false) => {
    try {
      let hostname;
      let title = customTitle;
      
      if (processedUrl.startsWith('data:')) {
        hostname = 'Search Results';
        // For search results, we don't want to update the displayUrl if it's already set
        if (!displayUrlValue) {
          displayUrlValue = `${searchQuery}`;
        }
      } else {
        const urlObj = new URL(processedUrl);
        hostname = urlObj.hostname;
        if (!customTitle) title = hostname;
        // For regular URLs, we want to show the actual URL
        if (!displayUrlValue) {
          displayUrlValue = processedUrl;
        }
      }
      
      // Set display URL
      setDisplayUrl(displayUrlValue);
      
      // Update tab data
      if (activeTabIndex >= 0 && activeTabIndex < tabs.length) {
        const updatedTabs = [...tabs];
        updatedTabs[activeTabIndex] = {
          ...updatedTabs[activeTabIndex],
          url: processedUrl,
          displayUrl: displayUrlValue,
          title: title,
          isHome: false,
          isSearch: isSearchPage,
          searchQuery: isSearchPage ? searchQuery : undefined
        };
        setTabs(updatedTabs);
        
        // Add to history only for real URLs or search pages
        if (!processedUrl.startsWith('data:') || isSearchPage) {
          addToHistory(processedUrl, title, isSearchPage);
        }
      }
      
      // Set title if provided
      if (customTitle) {
        setTitle(customTitle);
      }
      
      // Update navigation history
      // Only if we're not currently navigating through history
      if (processedUrl && !isShowingHome) {
        if (currentNavigationIndex >= -1 && currentNavigationIndex < navigationStack.length - 1) {
          // If we're not at the end of the stack, remove all entries after current index
          const newStack = navigationStack.slice(0, currentNavigationIndex + 1);
          newStack.push(processedUrl);
          setNavigationStack(newStack);
          setCurrentNavigationIndex(newStack.length - 1);
        } else {
          // Add new entry to the stack
          setNavigationStack([...navigationStack, processedUrl]);
          setCurrentNavigationIndex(navigationStack.length);
        }
        
        // Update navigation buttons state
        setCanGoBack(currentNavigationIndex >= 0 || navigationStack.length > 0);
        setCanGoForward(false);
      }
    } catch (error) {
      console.error('Error updating tab data:', error);
    }
  };

  // Function to generate HTML for search results
  const generateSearchResultsHtml = (data, query) => {
    // Extract relevant information from the DuckDuckGo API response
    const { AbstractText, AbstractURL, AbstractSource, RelatedTopics = [], Results = [] } = data;
    
    // Process and deduplicate results
    const allResults = [];
    const urlSet = new Set();
    
    // Process RelatedTopics and add unique ones
    RelatedTopics.forEach(topic => {
      if (topic.FirstURL && !urlSet.has(topic.FirstURL)) {
        allResults.push(topic);
        urlSet.add(topic.FirstURL);
      }
    });
    
    // Process Results and add unique ones
    Results.forEach(result => {
      if (result.FirstURL && !urlSet.has(result.FirstURL)) {
        allResults.push(result);
        urlSet.add(result.FirstURL);
      }
    });
    
    // Generate HTML
    let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Search results for: ${query}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
          margin: 0;
          padding: 20px;
          line-height: 1.6;
          color: #333;
          background-color: #f8f8f8;
        }
        .search-header {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e1e1e1;
        }
        .search-header h1 {
          font-size: 1.3rem;
          color: #222;
          margin: 0;
          font-weight: bold;
        }
        .abstract {
          background-color: white;
          padding: 20px;
          margin-bottom: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .abstract h2 {
          margin: 0 0 10px 0;
          font-size: 1.1rem;
          color: #1a73e8;
        }
        .abstract p {
          margin: 0 0 10px 0;
        }
        .abstract .source {
          font-size: 0.8rem;
          color: #666;
        }
        .search-result {
          background-color: white;
          margin-bottom: 15px;
          padding: 15px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .search-result h2 {
          margin: 0 0 5px 0;
          font-size: 1.1rem;
        }
        .search-result a {
          color: #1a73e8;
          text-decoration: none;
          display: block;
        }
        .search-result a:hover {
          text-decoration: underline;
        }
        .search-result p {
          margin: 5px 0;
          font-size: 0.9rem;
          color: #555;
        }
        .search-result .source {
          font-size: 0.8rem;
          color: #666;
        }
        .search-result .url {
          color: #1e8e3e;
          font-size: 0.85rem;
          margin-bottom: 5px;
        }
        .search-result .thumbnail {
          float: right;
          margin-left: 10px;
          margin-bottom: 10px;
          width: 70px;
          height: 70px;
          object-fit: cover;
          border-radius: 4px;
        }
        .category-header {
          font-size: 1.2rem;
          margin: 25px 0 15px 0;
          padding-top: 10px;
          border-top: 1px solid #e1e1e1;
          color: #333;
        }
        .footer {
          margin-top: 30px;
          padding: 20px 0;
        }
        .no-results {
          text-align: center;
          color: #555;
          margin: 50px 0;
          background-color: white;
          padding: 30px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .go-to {
          text-align: center;
          margin: 20px 0;
        }
        .go-to a {
          display: inline-block;
          padding: 10px 20px;
          background: #1a73e8;
          color: white;
          text-decoration: none;
          border-radius: 4px;
          font-weight: 500;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .go-to a:hover {
          background: #1558b7;
        }
      </style>
      <script>
        // This script handles link clicks in the search results
        document.addEventListener('DOMContentLoaded', function() {
          // Add click event listeners to all links
          document.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', function(e) {
              e.preventDefault();
              
              // Get the URL from the link
              const url = this.getAttribute('href');
              
              // Send a message to parent window
              window.parent.postMessage({
                type: 'SEARCH_RESULT_CLICK',
                url: url
              }, '*');
            });
          });
        });
      </script>
    </head>
    <body>
      <div class="search-header">
        <h1>Search results for: ${query}</h1>
      </div>
    `;
    
    // Add Abstract if available
    if (AbstractText && AbstractURL) {
      // Extract domain for the site title
      let siteName = AbstractSource;
      try {
        const urlObject = new URL(AbstractURL);
        const hostname = urlObject.hostname;
        if (!siteName) {
          siteName = hostname.replace(/^www\./, '');
        }
      } catch (e) {
        console.error('Error parsing URL:', e);
      }
      
      html += `
      <div class="abstract">
        <h2><a href="${AbstractURL}">${siteName}</a></h2>
        <p>${AbstractText}</p>
        <div class="source">Source: ${AbstractSource || 'Unknown'}</div>
      </div>
      `;
    }
    
    // Add results
    if (allResults.length > 0) {
      const processedResults = [];
      
      // First, filter out any DuckDuckGo results and extract direct links
      allResults.forEach(result => {
        const title = result.Text || result.Name || 'Result';
        const url = result.FirstURL || '#';
        
        // Skip invalid URLs or empty results
        if (url === '#') return;
        
        // Extract the actual URL from DuckDuckGo links
        let actualUrl = url;
        if (url.includes('duckduckgo.com')) {
          // Try to extract the real URL from the query string
          const match = url.match(/uddg=([^&]+)/);
          if (match && match[1]) {
            try {
              actualUrl = decodeURIComponent(match[1]);
            } catch (e) {
              console.error('Error decoding URL:', e);
            }
          } else {
            // Skip this result if it's just a DuckDuckGo link with no real URL
            return;
          }
        }
        
        // Generate clean data for display
        let displayUrl = actualUrl;
        let siteName = '';
        
        try {
          const urlObject = new URL(actualUrl);
          displayUrl = urlObject.href;
          siteName = urlObject.hostname.replace(/^www\./, '');
        } catch (e) {
          console.error('Error parsing URL:', e);
        }
        
        // Add to processed results if URL is unique
        if (!processedResults.some(r => r.url === actualUrl)) {
          processedResults.push({
            title: title,
            url: actualUrl,
            displayUrl: displayUrl,
            siteName: siteName,
            icon: result.Icon ? result.Icon.URL : null
          });
        }
      });
      
      // Now render the processed results
      processedResults.forEach(result => {
        let icon = '';
        if (result.icon) {
          icon = `<img src="${result.icon}" alt="" class="thumbnail">`;
        }
        
        html += `
        <div class="search-result">
          ${icon}
          <h2><a href="${result.url}">${result.title}</a></h2>
          <div class="url">${result.displayUrl}</div>
        </div>
        `;
      });
      
      // Add Official site result if we found a matching domain
      const queryLower = query.toLowerCase();
      const hasOfficialSite = processedResults.some(r => 
        r.url.includes(`//${queryLower}.`) || 
        r.url.includes(`//${queryLower}.org`) || 
        r.url.includes(`//${queryLower}.com`)
      );
      
      if (!hasOfficialSite) {
        // Try to guess an official site URL
        const potentialOfficialUrl = `https://${queryLower}.org`;
        html += `
        <div class="search-result">
          <h2><a href="${potentialOfficialUrl}">Official site - ${query}</a></h2>
          <div class="url">${potentialOfficialUrl}</div>
        </div>
        `;
      }
    } else if (!AbstractText) {
      html += `
      <div class="no-results">
        <p>No results found for "${query}"</p>
        <p>Try different keywords or check your spelling</p>
      </div>
      `;
    }
    
    // Add "See all results" at the bottom
    html += `
      <div class="footer">
        <div class="go-to">
          <a href="https://duckduckgo.com/?q=${encodeURIComponent(query)}">See all results on DuckDuckGo</a>
        </div>
      </div>
    </body>
    </html>
    `;
    
    return html;
  };

  const handleNavigate = async (targetUrl) => {
    console.log("Navigating to:", targetUrl);
    if (!targetUrl) return;
    
    let processedUrl = targetUrl.trim();
    let userFriendlyUrl = '';
    
    // If it has a protocol, it's definitely a URL
    if (processedUrl.startsWith('http://') || processedUrl.startsWith('https://')) {
      // It's already a properly formatted URL, navigate directly
      userFriendlyUrl = processedUrl;
      setUrl(processedUrl);
      setDisplayUrl(userFriendlyUrl);
      setIsLoading(true);
      setIsShowingHome(false);
      
      // Update tab data
      updateTabData(processedUrl, userFriendlyUrl);
      return;
    } else if (isValidDomain(processedUrl)) {
      // It looks like a domain (contains a period but no spaces and valid TLD)
      processedUrl = 'https://' + processedUrl;
      userFriendlyUrl = processedUrl;
      setUrl(processedUrl);
      setDisplayUrl(userFriendlyUrl);
      setIsLoading(true);
      setIsShowingHome(false);
      
      // Update tab data
      updateTabData(processedUrl, userFriendlyUrl);
      return;
    } else {
      // It's a search query - create a search results page
      setIsLoading(true);
      setIsShowingHome(false);
      
      // Store search query
      setSearchQuery(processedUrl);
      
      // Create a user-friendly display URL for search
      userFriendlyUrl = `${processedUrl}`;
      setDisplayUrl(userFriendlyUrl);
      
      // Set a temporary title
      setTitle(`Searching for: ${processedUrl}`);
      
      try {
        // Use DuckDuckGo API
        const searchUrl = `https://api.duckduckgo.com/?q=${encodeURIComponent(processedUrl)}&format=json&no_html=1&no_redirect=1&t=bitdog_browser`;

        
        // Fetch search results
        const response = await secureFetch(searchUrl);
        const data = await response.json();
        
        // Generate HTML for search results
        const searchResultsHtml = generateSearchResultsHtml(data, processedUrl);
        
        // Create a data URL with the search results HTML
        const searchResultsUrl = 'data:text/html;charset=utf-8,' + encodeURIComponent(searchResultsHtml);
        
        // Set the URL for the iframe to the data URL
        setUrl(searchResultsUrl);
        setTitle(`Search results for: ${processedUrl}`);
        
        // Update tab data - mark it as a search page
        updateTabData(searchResultsUrl, userFriendlyUrl, `Search results for: ${processedUrl}`, true);
      } catch (error) {
        console.error('Error fetching search results:', error);
        // Fallback to direct DuckDuckGo search if API fails
        const fallbackUrl = `https://duckduckgo.com/?q=${encodeURIComponent(processedUrl)}`;
        setUrl(fallbackUrl);
        setDisplayUrl(fallbackUrl);
        updateTabData(fallbackUrl, fallbackUrl, `Search results for: ${processedUrl}`, false);
      }
    }
  };

  // Custom navigation functions that use our navigation stack
  const handleGoBack = () => {
    if (isShowingHome || navigationStack.length === 0 || currentNavigationIndex <= 0) {
      // If we're already at the beginning of navigation history, do nothing
      return;
    }
    
    // Decrement the navigation index and load the previous URL
    const newIndex = currentNavigationIndex - 1;
    const previousUrl = navigationStack[newIndex];
    
    if (previousUrl) {
      setIsLoading(true);
      setUrl(previousUrl);
      setCurrentNavigationIndex(newIndex);
      
      // Update the navigation state
      setCanGoBack(newIndex > 0);
      setCanGoForward(true);
    }
  };

  const handleGoForward = () => {
    if (isShowingHome || currentNavigationIndex >= navigationStack.length - 1) {
      // If we're already at the end of navigation history, do nothing
      return;
    }
    
    // Increment the navigation index and load the next URL
    const newIndex = currentNavigationIndex + 1;
    const nextUrl = navigationStack[newIndex];
    
    if (nextUrl) {
      setIsLoading(true);
      setUrl(nextUrl);
      setCurrentNavigationIndex(newIndex);
      
      // Update the navigation state
      setCanGoBack(true);
      setCanGoForward(newIndex < navigationStack.length - 1);
    }
  };

  const handleRefresh = () => {
    if (isShowingHome) {
      // Just re-render the home screen
      setIsShowingHome(true);
    } else if (iframeRef.current) {
      // Force iframe to reload by setting the same URL again
      const currentSrc = iframeRef.current.src;
      iframeRef.current.src = "about:blank";
      
      // Use setTimeout to ensure about:blank is loaded before setting the actual URL
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = currentSrc;
          setIsLoading(true);
        }
      }, 50);
    }
  };

  const handleHome = () => {
    setIsShowingHome(true);
    
    // Update the current tab to be a home tab
    if (activeTabIndex >= 0 && activeTabIndex < tabs.length) {
      const updatedTabs = [...tabs];
      updatedTabs[activeTabIndex] = {
        ...updatedTabs[activeTabIndex],
        isHome: true
      };
      setTabs(updatedTabs);
      
      // Reset the navigation for this tab
      setNavigationStack([]);
      setCurrentNavigationIndex(-1);
      setCanGoBack(false);
      setCanGoForward(false);
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    
    try {
      // Update title and favicon if possible
      if (iframeRef.current && iframeRef.current.contentDocument) {
        const newTitle = iframeRef.current.contentDocument.title;
        setTitle(newTitle);
        
        // Try to get favicon
        let favicon = null;
        const faviconEl = iframeRef.current.contentDocument.querySelector('link[rel="icon"], link[rel="shortcut icon"]');
        if (faviconEl) {
          favicon = faviconEl.href;
        } else if (url) {
          try {
            // Skip favicon for data URLs (search results)
            if (!url.startsWith('data:')) {
              const hostname = new URL(url).hostname;
              favicon = `https://${hostname}/favicon.ico`;
            }
          } catch (error) {
            console.error('Error parsing URL for favicon:', error);
          }
        }
        
        // Update tab favicon
        if (favicon && activeTabIndex >= 0 && activeTabIndex < tabs.length) {
          const updatedTabs = [...tabs];
          updatedTabs[activeTabIndex] = {
            ...updatedTabs[activeTabIndex],
            favicon,
            title: newTitle || updatedTabs[activeTabIndex].title,
          };
          setTabs(updatedTabs);
        }
        
        // Add to history if this is a real URL (not about:blank or data:)
        if (url && url !== 'about:blank' && !url.startsWith('data:')) {
          addToHistory(url, newTitle);
        }
      } else {
        // For cross-origin iframes, we can't access the content
        // But we can still update the tab with the basic info we have
        if (url && url !== 'about:blank' && !url.startsWith('data:') && activeTabIndex >= 0 && activeTabIndex < tabs.length) {
          try {
            const hostname = new URL(url).hostname;
            const defaultTitle = hostname;
            const defaultFavicon = `https://${hostname}/favicon.ico`;
            
            // Update tab with basic info
            const updatedTabs = [...tabs];
            // Keep existing title if it's already set, otherwise use hostname
            const tabTitle = updatedTabs[activeTabIndex].title || defaultTitle;
            
            updatedTabs[activeTabIndex] = {
              ...updatedTabs[activeTabIndex],
              url,
              displayUrl: displayUrl || url,
              title: tabTitle,
              favicon: updatedTabs[activeTabIndex].favicon || defaultFavicon,
              isHome: false
            };
            setTabs(updatedTabs);
            
            // Also update the title state
            if (!title) {
              setTitle(tabTitle);
            }
            
            // Add to history
            addToHistory(url, tabTitle);
          } catch (error) {
            console.error('Error updating tab with basic info:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error accessing iframe content:', error);
      // This is expected for cross-origin iframes due to security restrictions
      
      // Still try to update tab information with the URL we have
      if (url && url !== 'about:blank' && !url.startsWith('data:') && activeTabIndex >= 0 && activeTabIndex < tabs.length) {
        try {
          const hostname = new URL(url).hostname;
          
          // Update tab with basic info
          const updatedTabs = [...tabs];
          updatedTabs[activeTabIndex] = {
            ...updatedTabs[activeTabIndex],
            url: url,
            displayUrl: displayUrl || url,
            title: updatedTabs[activeTabIndex].title || hostname,
            isHome: false
          };
          setTabs(updatedTabs);
          
          // Add to history
          addToHistory(url, updatedTabs[activeTabIndex].title || hostname);
        } catch (error) {
          console.error('Error updating tab with URL info after security error:', error);
        }
      }
    }
  };

  // Tab management functions
  const handleTabSelect = (index) => {
    if (index >= 0 && index < tabs.length) {
      setActiveTabIndex(index);
      const selectedTab = tabs[index];
      
      if (selectedTab.isHome) {
        setIsShowingHome(true);
        
        // Reset navigation state for home tab
        setNavigationStack([]);
        setCurrentNavigationIndex(-1);
        setCanGoBack(false);
        setCanGoForward(false);
      } else {
        // Only set URL and title if they exist
        if (selectedTab.url) {
          setUrl(selectedTab.url);
          setDisplayUrl(selectedTab.displayUrl || selectedTab.url);
          setTitle(selectedTab.title || '');
          setIsShowingHome(false);
          setIsLoading(true);
          
          // Set search query if it's a search tab
          if (selectedTab.isSearch && selectedTab.searchQuery) {
            setSearchQuery(selectedTab.searchQuery);
          }
          
          // Reset navigation state for this tab
          // In a real implementation, you'd store navigation per tab
          setNavigationStack([selectedTab.url]);
          setCurrentNavigationIndex(0);
          setCanGoBack(false);
          setCanGoForward(false);
        } else {
          // If tab has no URL, treat it as home
          setIsShowingHome(true);
          
          // Update the tab to be a home tab
          const updatedTabs = [...tabs];
          updatedTabs[index] = {
            ...updatedTabs[index],
            isHome: true
          };
          setTabs(updatedTabs);
          
          // Reset navigation state
          setNavigationStack([]);
          setCurrentNavigationIndex(-1);
          setCanGoBack(false);
          setCanGoForward(false);
        }
      }
    }
  };

  const handleTabClose = (index) => {
    if (tabs.length <= 1) {
      // Don't close the last tab, reset it to home instead
      handleHome();
      return;
    }
    
    const newTabs = [...tabs];
    newTabs.splice(index, 1);
    setTabs(newTabs);
    
    // If we're closing the active tab, switch to the previous one or the next one
    if (index === activeTabIndex) {
      const newIndex = index > 0 ? index - 1 : 0;
      setActiveTabIndex(newIndex);
      
      const newActiveTab = newTabs[newIndex];
      if (newActiveTab.isHome) {
        setIsShowingHome(true);
        
        // Reset navigation state
        setNavigationStack([]);
        setCurrentNavigationIndex(-1);
        setCanGoBack(false);
        setCanGoForward(false);
      } else {
        setUrl(newActiveTab.url || '');
        setDisplayUrl(newActiveTab.displayUrl || newActiveTab.url || '');
        setTitle(newActiveTab.title || '');
        setIsShowingHome(false);
        setIsLoading(true);
        
        // Set search query if it's a search tab
        if (newActiveTab.isSearch && newActiveTab.searchQuery) {
          setSearchQuery(newActiveTab.searchQuery);
        }
        
        // Reset navigation state for this tab
        if (newActiveTab.url) {
          setNavigationStack([newActiveTab.url]);
          setCurrentNavigationIndex(0);
          setCanGoBack(false);
          setCanGoForward(false);
        }
      }
    } else if (index < activeTabIndex) {
      // If we're closing a tab before the active one, decrement the active index
      setActiveTabIndex(activeTabIndex - 1);
    }
  };

  const handleNewTab = () => {
    const newTab = { url: '', displayUrl: '', title: '', favicon: null, isHome: true, isSearch: false };
    const newIndex = tabs.length;
    setTabs([...tabs, newTab]);
    setActiveTabIndex(newIndex);
    setIsShowingHome(true);
    
    // Reset URL and title for the new tab
    setUrl('');
    setDisplayUrl('');
    setTitle('');
    
    // Reset navigation state for the new tab
    setNavigationStack([]);
    setCurrentNavigationIndex(-1);
    setCanGoBack(false);
    setCanGoForward(false);
  };

  const handleAddFavorite = () => {
    if (isShowingHome || !url) return;
    
    try {
      // Get stored favorites
      const savedFavorites = localStorage.getItem('bitdog_browser_favorites');
      let favorites = savedFavorites ? JSON.parse(savedFavorites) : {};
      
      // If this is a search page, don't add to favorites
      if (url.startsWith('data:')) return;
      
      // Make sure we have the current chain
      if (!currentChain || !currentChain.key) {
        console.error("Cannot add favorite: missing current chain");
        return;
      }
      
      // Initialize chain favorites if it doesn't exist
      if (!favorites[currentChain.key]) {
        favorites[currentChain.key] = [];
      }
      
      const hostname = new URL(url).hostname;
      
      // Check if URL already exists in favorites for this chain
      const existingIndex = favorites[currentChain.key].findIndex(item => item.url === url);
      
      if (existingIndex === -1) {
        // Add to favorites for current chain
        const newFavorite = {
          url,
          name: title || hostname,
          icon: `https://${hostname}/favicon.ico`
        };
        
        favorites[currentChain.key].push(newFavorite);
        localStorage.setItem('bitdog_browser_favorites', JSON.stringify(favorites));
      }
    } catch (error) {
      console.error('Error adding to favorites:', error);
    }
  };

  // Ensure wallet is available (keep this check)
  if (!currentWallet || !currentWallet.address) {
    return (
      <div className="browser-no-wallet">
        <h2>{t('browser.noWallet.title')}</h2>
        <p>{t('browser.noWallet.message')}</p>
        <button onClick={() => navigate('/wallet')}>
          {t('browser.noWallet.action')}
        </button>
      </div>
    );
  }

  // Wrap the iframe with a div that will capture clicks
  const handleWrapperClick = (e) => {
    // If clicking on a link inside the iframe, we want to intercept that
    // but this is handled by our iframe event listener
    
    // This wrapper click handler is a backup to ensure we capture all interactions
    if (e.target.tagName === 'A') {
      e.preventDefault();
      
      if (e.target.href && e.target.href !== 'javascript:void(0)' && !e.target.href.startsWith('about:')) {
        handleNavigate(e.target.href);
      }
      
      return false;
    }
  };

  return (
    <div className="browser-container">
      <BrowserHeader 
        url={displayUrl || url} // Use the display URL for the header
        title={title}
        isLoading={isLoading}
        onUrlChange={setUrl}
        onNavigate={handleNavigate}
        isShowingHome={isShowingHome}
      />
      
      {isShowingHome ? (
        <BrowserHome onNavigate={handleNavigate} />
      ) : (
        <div className="browser-content">
          <div className="iframe-container" ref={iframeContainerRef} onClick={handleWrapperClick}>
            <iframe
              ref={iframeRef}
              src={url}
              title="Web Browser"
              className={`browser-iframe ${isLoading ? 'loading' : ''}`}
              style={{
                touchAction: 'auto',
                WebkitOverflowScrolling: 'touch',
                overflow: 'auto',
                pointerEvents: 'auto'
              }}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads"
              onLoad={handleIframeLoad}
            />
          </div>
          
          {isLoading && (
            <div className="browser-loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">{t('browser.loading')}</div>
            </div>
          )}
        </div>
      )}

      <BrowserNavBar
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onGoBack={handleGoBack}
        onGoForward={handleGoForward}
        onRefresh={handleRefresh}
        onHome={handleHome}
        isShowingHome={isShowingHome}
        tabs={tabs}
        activeTabIndex={activeTabIndex}
        onTabSelect={handleTabSelect}
        onTabClose={handleTabClose}
        onNewTab={handleNewTab}
        onAddFavorite={handleAddFavorite}
      />
      
    </div>
  );
};

export default Browser;